import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  LuActivity,
  LuTriangleAlert,
  LuArrowDownRight,
  LuArrowRight,
  LuArrowUpRight,
  LuBatteryWarning,
  LuBoxes,
  LuBuilding2,
  LuCheck,
  LuDownload,
  LuFactory,
  LuMapPin,
  LuPackage,
  LuPencil,
  LuPlane,
  LuPlus,
  LuSearch,
  LuShieldCheck,
  LuSignal,
  LuTrash2,
  LuX,
} from "react-icons/lu";
import {
  FIELD_MAX_LENGTHS,
  FIELDS,
  MODULES,
  PAGE_SIZE,
  WORKSPACE_COLLECTIONS,
  emptyDb,
} from "../config/workspaceConfig";
import {
  dateOnly,
  dateTime,
  formatRemainingTime,
  formatTime,
  idLabel,
  isDateTimeLocal,
  isValidDateOnly,
  isValidDateTimeLocal,
  isValidPhoneNumber,
  loadBagCounter,
  moduleRecordLabel,
  normalizePhoneNumber,
  recordStatus,
  rowValue,
  uid,
} from "../utils/workspaceUtils";
import { exportWorkspacePdf } from "../utils/exportWorkspacePdf";

import { getWorkspace, saveWorkspaceModules } from "../services/adminApi";
import AdminProfilePage from "../components/AdminProfilePage";
import AdminSidebar from "../components/AdminSidebar";
import { useAdminToast } from "../components/AdminToastProvider";
import AdminTopbar from "../components/AdminTopbar";
import AdminWorkspaceLoading from "../components/AdminWorkspaceLoading";
import "../styles/admin.css";
import "../styles/admin-workspace.css";

function AdminWorkspacePage({ authToken, user, onLogout }) {
  const notify = useAdminToast();
  const location = useLocation();
  const navigate = useNavigate();
  const routeKey =
    location.pathname.split("/").filter(Boolean)[1] || "dashboard";
  const isProfileRoute = routeKey === "profile";
  const activeModule =
    MODULES.find((module) => module.key === routeKey) || MODULES[0];
  const [db, setDb] = useState(emptyDb);
  const [category, setCategory] = useState("Domestic");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All statuses");
  const [page, setPage] = useState(1);
  const [formItem, setFormItem] = useState(null);
  const [editingId, setEditingId] = useState("");
  const [viewItem, setViewItem] = useState(null);
  const [deleteItem, setDeleteItem] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [bagCounter, setBagCounter] = useState(loadBagCounter);
  const [counterTarget, setCounterTarget] = useState(() =>
    String(bagCounter.target),
  );
  const [counterDuration, setCounterDuration] = useState(() =>
    String(bagCounter.durationMinutes),
  );
  const [counterNow, setCounterNow] = useState(Date.now());
  const [dbReady, setDbReady] = useState(false);
  const [dbLoadError, setDbLoadError] = useState("");
  const [workspaceLoadAttempt, setWorkspaceLoadAttempt] = useState(0);
  const invalidToastPending = useRef(false);
  const loadedCategory = useRef(category);

  const showValidationToast = (event) => {
    event.preventDefault();
    if (invalidToastPending.current) return;
    invalidToastPending.current = true;
    notify(
      event.target.validationMessage || "Please check the form fields.",
      "error",
    );
    window.setTimeout(() => {
      invalidToastPending.current = false;
    }, 0);
  };

  useEffect(() => {
    let isCurrent = true;
    const syncWorkspace = async () => {
      setDbLoadError("");
      try {
        const result = await getWorkspace(authToken, category);
        const workspaceData = result.data;
        if (!isCurrent) return;
        const loadedDb = { ...emptyDb(), ...workspaceData };
        setDb(loadedDb);
        loadedCategory.current = category;
        if (loadedDb.liveBagCounter) {
          setBagCounter(loadedDb.liveBagCounter);
          setCounterTarget(String(loadedDb.liveBagCounter.target));
          setCounterDuration(String(loadedDb.liveBagCounter.durationMinutes));
        }
        setDbReady(true);
      } catch (error) {
        if (!isCurrent) return;
        if (error.status === 401) {
          onLogout(error.message);
          return;
        }
        if (loadedCategory.current !== category) {
          setCategory(loadedCategory.current);
        }
        setDbLoadError(error.message);
        notify(
          error.message || "Unable to load admin workspace data.",
          "error",
        );
      }
    };
    syncWorkspace();
    return () => {
      isCurrent = false;
    };
  }, [authToken, category, onLogout, notify, workspaceLoadAttempt]);

  useEffect(() => {
    if (bagCounter.startedAt === null) return undefined;
    const durationMs = bagCounter.durationMinutes * 60 * 1000;
    const updateCounter = () => {
      const now = Date.now();
      setCounterNow(now);
      if (now - bagCounter.startedAt >= durationMs)
        window.clearInterval(interval);
    };
    const interval = window.setInterval(updateCounter, 1000);
    return () => window.clearInterval(interval);
  }, [bagCounter.startedAt, bagCounter.durationMinutes]);

  const startBagCounter = async (event) => {
    event.preventDefault();
    const target = Number(counterTarget);
    const durationMinutes = Number(counterDuration);
    if (
      !Number.isSafeInteger(target) ||
      target < 1 ||
      !Number.isSafeInteger(durationMinutes) ||
      durationMinutes < 1 ||
      !Number.isSafeInteger(durationMinutes * 60 * 1000)
    ) {
      notify(
        "Enter a whole bag target and a duration of at least 1 minute.",
        "error",
      );
      return;
    }
    const nextCounter = { target, durationMinutes, startedAt: Date.now() };
    try {
      const saved = await saveWorkspaceModules(
        authToken,
        { liveBagCounter: nextCounter },
        category,
      );
      setDb({ ...emptyDb(), ...saved.data });
      setBagCounter(nextCounter);
      setCounterNow(nextCounter.startedAt);
      notify("Live bag counter started successfully.", "success");
    } catch (error) {
      if (error.status === 401) {
        onLogout(error.message);
        return;
      }
      console.error("Unable to save live bag counter settings.", error);
      notify(
        error.message || "Unable to save live bag counter settings.",
        "error",
      );
    }
  };

  const counterDurationMs = bagCounter.durationMinutes * 60 * 1000;
  const counterElapsedMs =
    bagCounter.startedAt === null
      ? 0
      : Math.max(0, counterNow - bagCounter.startedAt);
  const liveBagCount =
    bagCounter.startedAt === null
      ? 0
      : Math.min(
          bagCounter.target,
          Math.floor(
            (counterElapsedMs / counterDurationMs) * bagCounter.target,
          ),
        );
  const counterProgress =
    bagCounter.startedAt === null
      ? 0
      : Math.min(100, (counterElapsedMs / counterDurationMs) * 100);
  const counterRemainingMs = Math.max(0, counterDurationMs - counterElapsedMs);
  const isCounterRunning =
    bagCounter.startedAt !== null && counterRemainingMs > 0;

  useEffect(() => {
    if (
      routeKey !== "profile" &&
      !MODULES.some((module) => module.key === routeKey)
    )
      navigate("/admin/dashboard", { replace: true });
  }, [routeKey, navigate]);

  useEffect(() => {
    setSearch("");
    setStatusFilter("All statuses");
    setPage(1);
  }, [routeKey, category]);
  const airportsInCategory = db.airports;
  const airportIds = useMemo(
    () => new Set(airportsInCategory.map((airport) => airport.id)),
    [airportsInCategory],
  );

  const records = useMemo(() => {
    if (activeModule.key === "dashboard") return [];
    if (activeModule.key === "beltRunning")
      return db.belts.map((belt) => ({
        ...belt,
        id: belt.id,
        beltId: belt.id,
        beltName: belt.name,
        airportId: belt.airportId,
        lastUpdated: belt.lastUpdated || dateTime(0.1),
      }));
    let source = db[activeModule.key] || [];
    if (activeModule.key === "systems" && source.length === 0) source = [];
    if (activeModule.key === "countries" || activeModule.key === "states") {
      source = source.filter(Boolean);
    } else if (activeModule.key === "iataCodes") {
      source = source.filter((item) => airportIds.has(item.airportId));
    } else if (activeModule.key === "approaches") {
      source = source.filter((item) => airportIds.has(item.airportId));
    } else if (activeModule.key !== "systems") {
      source = source.filter(
        (item) => !item.airportId || airportIds.has(item.airportId),
      );
    }
    return source;
  }, [db, activeModule, airportIds]);

  const pageFields = FIELDS[activeModule.key] || [];
  const moduleHasStatus = pageFields.some(
    (field) =>
      field[0] === "status" ||
      field[0] === "runningStatus" ||
      field[0] === "connectionStatus" ||
      field[0] === "condition" ||
      field[0] === "faultStatus" ||
      field[0] === "deviceStatus" ||
      field[0] === "systemStatus",
  );

  const filteredRecords = useMemo(
    () =>
      records.filter((item) => {
        const query = search.trim().toLowerCase();
        const matchedSearch =
          !query ||
          Object.values(item).some((value) =>
            String(value ?? "")
              .toLowerCase()
              .includes(query),
          );
        if (!matchedSearch) return false;
        if (statusFilter === "All statuses") return true;
        const status = recordStatus(activeModule, item);
        return status.toLowerCase() === statusFilter.toLowerCase();
      }),
    [records, search, statusFilter, activeModule],
  );

  useEffect(() => setPage(1), [search, statusFilter, routeKey]);
  const totalPages = Math.max(1, Math.ceil(filteredRecords.length / PAGE_SIZE));
  const pageRecords = filteredRecords.slice(
    (page - 1) * PAGE_SIZE,
    page * PAGE_SIZE,
  );

  const filteredOptions = (moduleKey, field, values) => {
    if (field === "countryId") return db.countries;
    if (field === "stateId") {
      const countryId = values.countryId || values.country;
      return db.states.filter(
        (item) => !countryId || item.countryId === countryId,
      );
    }
    if (field === "airportId") {
      return db.airports.filter(
        (item) =>
          (!values.countryId || item.countryId === values.countryId) &&
          (!values.stateId || item.stateId === values.stateId),
      );
    }
    if (field === "approachId")
      return db.approaches.filter(
        (item) =>
          (!values.airportId || item.airportId === values.airportId) &&
          (!values.stateId || item.stateId === values.stateId),
      );
    if (field === "beltId")
      return db.belts.filter(
        (item) =>
          (!values.airportId || item.airportId === values.airportId) &&
          (!values.approachId || item.approachId === values.approachId),
      );
    if (field === "plcId")
      return db.plcs.filter(
        (item) => !values.airportId || item.airportId === values.airportId,
      );
    if (field === "sensorId")
      return db.sensors.filter(
        (item) => !values.beltId || item.beltId === values.beltId,
      );
    return db[moduleKey] || [];
  };

  const recordContext = (item) => {
    if (!item) return {};
    if (activeModule.key === "beltRunning")
      return db.belts.find((belt) => belt.id === item.beltId) || item;
    return item;
  };

  const persist = async (
    nextDb,
    action,
    entity,
    moduleKey = activeModule.key,
  ) => {
    const stamp = new Date().toISOString();
    nextDb.activity = [
      {
        id: uid("activity"),
        action,
        entity,
        module: moduleKey,
        createdAt: stamp,
        severity: "Info",
        createdBy: "Administrator",
      },
      ...nextDb.activity,
    ].slice(0, 100);
    try {
      const changedModules = Object.fromEntries(
        WORKSPACE_COLLECTIONS.filter(
          (collection) =>
            JSON.stringify(nextDb[collection]) !==
            JSON.stringify(db[collection]),
        ).map((collection) => [collection, nextDb[collection]]),
      );
      const saved = await saveWorkspaceModules(
        authToken,
        changedModules,
        category,
      );
      setDb({ ...emptyDb(), ...saved.data });
      notify(`${entity} ${action.toLowerCase()} successfully.`, "success");
      return true;
    } catch (error) {
      if (error.status === 401) {
        onLogout(error.message);
        return false;
      }
      console.error("Unable to save admin workspace changes.", error);
      notify(
        error.message || "Unable to save admin workspace changes.",
        "error",
      );
      return false;
    }
  };

  const fieldInitial = (field) => {
    if (activeModule.key === "airports" && field[0] === "type") return category;
    if (activeModule.key === "countries" && field[0] === "classification")
      return category === "Domestic" ? "Domestic base" : "International";
    if (activeModule.key === "bagCounts" && field[0] === "countSource")
      return "Manual entry";
    if (activeModule.key === "cellPhones" && field[0] === "notifications")
      return "All alerts";
    if (activeModule.key === "cellPhones" && field[0] === "connectionStatus")
      return "Connected";
    if (field[0] === "status") return "Active";
    if (field[0] === "runningStatus") return "Stopped";
    if (field[0] === "connectionStatus") return "Online";
    if (field[0] === "faultStatus") return "Clear";
    if (field[0] === "condition") return "Good";
    if (field[0] === "deviceStatus") return "Available";
    if (field[0] === "notifications") return "Enabled";
    if (field[0] === "integrationStatus") return "Not connected";
    if (field[0] === "backupSchedule") return "Daily";
    if (field[0] === "systemStatus") return "Operational";
    if (field[2] === "datetime-local") return dateTime();
    if (field[2] === "date") return dateOnly();
    return "";
  };

  const openForm = (item) => {
    const editableRecord = item ? recordContext(item) : {};
    setEditingId(item?.id || "");
    setFormItem(
      Object.fromEntries(
        pageFields.map((field) => [
          field[0],
          editableRecord[field[0]] ?? fieldInitial(field),
        ]),
      ),
    );
  };
  const closeForm = () => {
    setFormItem(null);
    setEditingId("");
  };

  const validateRecord = (values) => {
    const fields = FIELDS[activeModule.key] || [];
    for (const [key, label, type, required, options] of fields) {
      const value = values[key];
      const isBlank =
        value === undefined ||
        value === null ||
        (typeof value === "string" && !value.trim());
      if (required && isBlank) return `${label} is required.`;
      if (isBlank) continue;

      if (typeof value === "string") {
        const maxLength = FIELD_MAX_LENGTHS[activeModule.key]?.[key];
        if (maxLength && value.length > maxLength)
          return `${label} must be ${maxLength} characters or less.`;
      }
      if (
        type === "number" &&
        (!Number.isFinite(Number(value)) || Number(value) < 0)
      ) {
        return `${label} must be a valid non-negative number.`;
      }
      if (
        type === "email" &&
        (String(value).length > 254 ||
          !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value).trim()))
      ) {
        return `${label} must be a valid email address of 254 characters or less.`;
      }
      if (type === "date" && !isValidDateOnly(value))
        return `${label} must be a valid date.`;
      if (type === "datetime-local" && !isValidDateTimeLocal(value))
        return `${label} must be a valid date and time.`;
      if (type === "select") {
        const allowedOptions =
          activeModule.key === "airports" && key === "type"
            ? [category]
            : activeModule.key === "countries" && key === "classification"
              ? [category === "Domestic" ? "Domestic base" : "International"]
              : options;
        if (!allowedOptions.includes(value))
          return `Choose a valid ${label.toLowerCase()}.`;
      }
      if (type === "relation") {
        const relatedRecords = filteredOptions(activeModule.key, key, values);
        if (!relatedRecords.some((record) => record.id === value))
          return `Choose a valid ${label.toLowerCase()}.`;
      }
    }

    const duplicate = (
      key,
      message,
      normalize = (value) => String(value || "").toLowerCase(),
    ) => {
      const validationRecords = db[activeModule.key] || records;
      if (
        validationRecords.some(
          (record) =>
            record.id !== editingId &&
            normalize(record[key]) === normalize(values[key]),
        )
      )
        return message;
      return "";
    };
    if (activeModule.key === "iataCodes") {
      if (!/^[A-Z]{3}$/.test(values.code || ""))
        return "IATA code must be exactly three English letters.";
      const duplicateCode = duplicate(
        "code",
        "This IATA code already belongs to another airport.",
        (value) => String(value || "").toUpperCase(),
      );
      if (duplicateCode) return duplicateCode;
      if (
        db.iataCodes.some(
          (record) =>
            record.id !== editingId && record.airportId === values.airportId,
        )
      )
        return "This airport already has an IATA code.";
    }
    if (activeModule.key === "countries") {
      if (!/^[A-Z]{2,3}$/.test(values.isoCode || ""))
        return "ISO code must be 2 or 3 English letters.";
      if (values.name && values.name.length > 200)
        return "Country name must be 200 characters or less.";
      if (values.classification && values.classification.length > 50)
        return "Classification must be 50 characters or less.";
      if (values.phoneCode && values.phoneCode.length > 20)
        return "Phone code must be 20 characters or less.";
      if (values.currency && values.currency.length > 10)
        return "Currency must be 10 characters or less.";
    }
    if (activeModule.key === "states") {
      if (values.name && values.name.length > 200)
        return "State name must be 200 characters or less.";
      if (!/^[A-Z0-9]{1,20}$/.test(values.code || ""))
        return "State code must be 1-20 uppercase letters or numbers.";
      if (values.countryId && values.countryId.length > 100)
        return "Country ID must be 100 characters or less.";
    }
    if (activeModule.key === "airports") {
      if (values.name && values.name.length > 200)
        return "Airport name must be 200 characters or less.";
      if (values.type && !["Domestic", "International"].includes(values.type))
        return "Airport type must be Domestic or International.";
      if (values.countryId && values.countryId.length > 100)
        return "Country ID must be 100 characters or less.";
      if (values.stateId && values.stateId.length > 100)
        return "State ID must be 100 characters or less.";
      if (values.city && values.city.length > 150)
        return "City must be 150 characters or less.";
      if (values.address && values.address.length > 2000)
        return "Address must be 2000 characters or less.";
    }
    if (activeModule.key === "approaches") {
      if (values.name && values.name.length > 200)
        return "Approach name must be 200 characters or less.";
      if (values.code && values.code.length > 50)
        return "Approach code must be 50 characters or less.";
      if (values.countryId && values.countryId.length > 100)
        return "Country ID must be 100 characters or less.";
      if (values.stateId && values.stateId.length > 100)
        return "State ID must be 100 characters or less.";
      if (values.airportId && values.airportId.length > 100)
        return "Airport ID must be 100 characters or less.";
      if (values.terminal && values.terminal.length > 100)
        return "Terminal must be 100 characters or less.";
      if (values.zone && values.zone.length > 200)
        return "Zone must be 200 characters or less.";
      if (values.description && values.description.length > 2000)
        return "Description must be 2000 characters or less.";
    }
    if (activeModule.key === "belts") {
      if (values.beltId && values.beltId.length > 100)
        return "Belt ID must be 100 characters or less.";
      if (values.name && values.name.length > 200)
        return "Belt name must be 200 characters or less.";
      if (values.countryId && values.countryId.length > 100)
        return "Country ID must be 100 characters or less.";
      if (values.stateId && values.stateId.length > 100)
        return "State ID must be 100 characters or less.";
      if (values.airportId && values.airportId.length > 100)
        return "Airport ID must be 100 characters or less.";
      if (values.terminal && values.terminal.length > 100)
        return "Terminal must be 100 characters or less.";
      if (values.approachId && values.approachId.length > 100)
        return "Approach ID must be 100 characters or less.";
      if (
        values.type &&
        !["Arrival", "Departure", "Transfer", "Reclaim"].includes(values.type)
      )
        return "Belt type must be Arrival, Departure, Transfer, or Reclaim.";
      if (
        values.installationDate &&
        !/^\d{4}-\d{2}-\d{2}$/.test(values.installationDate)
      )
        return "Installation date must be in YYYY-MM-DD format.";
      if (
        values.runningStatus &&
        ![
          "Running",
          "Stopped",
          "Fault",
          "Maintenance",
          "Offline / Unknown",
        ].includes(values.runningStatus)
      )
        return "Running status must be one of: Running, Stopped, Fault, Maintenance, Offline / Unknown.";
      if (values.sensorId && values.sensorId.length > 100)
        return "Sensor ID must be 100 characters or less.";
      if (values.plcId && values.plcId.length > 100)
        return "PLC ID must be 100 characters or less.";
      if (values.lastUpdated && !isDateTimeLocal(values.lastUpdated))
        return "Last updated must be a valid date and time.";
    }
    if (activeModule.key === "bagCounts") {
      if (values.airportId && values.airportId.length > 100)
        return "Airport ID must be 100 characters or less.";
      if (values.terminal && values.terminal.length > 100)
        return "Terminal must be 100 characters or less.";
      if (values.beltId && values.beltId.length > 100)
        return "Belt ID must be 100 characters or less.";
      if (values.sensorId && values.sensorId.length > 100)
        return "Sensor ID must be 100 characters or less.";
      if (
        values.currentCount !== undefined &&
        (isNaN(values.currentCount) || values.currentCount < 0)
      )
        return "Current count must be a non-negative number.";
      if (
        values.totalCount !== undefined &&
        (isNaN(values.totalCount) || values.totalCount < 0)
      )
        return "Total count must be a non-negative number.";
      if (values.recordedAt && !isDateTimeLocal(values.recordedAt))
        return "Recorded at must be a valid date and time.";
      if (values.eventId && values.eventId.length > 200)
        return "Event ID must be 200 characters or less.";
      if (values.flightNumber && values.flightNumber.length > 30)
        return "Flight number must be 30 characters or less.";
      if (
        values.countSource &&
        !["Sensor / PLC", "Manual entry", "Import"].includes(values.countSource)
      )
        return "Count source must be Sensor / PLC, Manual entry, or Import.";
    }
    if (activeModule.key === "sensors") {
      if (values.sensorId && values.sensorId.length > 100)
        return "Sensor ID must be 100 characters or less.";
      if (values.name && values.name.length > 200)
        return "Sensor name must be 200 characters or less.";
      if (
        values.type &&
        !["Photo-eye", "Proximity", "Barcode", "Weighing", "Other"].includes(
          values.type,
        )
      )
        return "Sensor type must be Photo-eye, Proximity, Barcode, Weighing, or Other.";
      if (values.airportId && values.airportId.length > 100)
        return "Airport ID must be 100 characters or less.";
      if (values.approachId && values.approachId.length > 100)
        return "Approach ID must be 100 characters or less.";
      if (values.beltId && values.beltId.length > 100)
        return "Belt ID must be 100 characters or less.";
      if (values.plcId && values.plcId.length > 100)
        return "PLC ID must be 100 characters or less.";
      if (
        values.connectionStatus &&
        !["Online", "Offline"].includes(values.connectionStatus)
      )
        return "Connection status must be Online or Offline.";
      if (values.lastSignal && !isDateTimeLocal(values.lastSignal))
        return "Last signal must be a valid date and time.";
      if (
        values.faultStatus &&
        !["Clear", "Warning", "Fault"].includes(values.faultStatus)
      )
        return "Fault status must be Clear, Warning, or Fault.";
    }
    if (activeModule.key === "bearings") {
      if (values.bearingId && values.bearingId.length > 100)
        return "Bearing ID must be 100 characters or less.";
      if (values.type && values.type.length > 100)
        return "Bearing type must be 100 characters or less.";
      if (values.beltId && values.beltId.length > 100)
        return "Belt ID must be 100 characters or less.";
      if (values.location && values.location.length > 200)
        return "Location must be 200 characters or less.";
      if (
        values.installationDate &&
        !/^\d{4}-\d{2}-\d{2}$/.test(values.installationDate)
      )
        return "Installation date must be in YYYY-MM-DD format.";
      if (
        values.lastInspectionDate &&
        !/^\d{4}-\d{2}-\d{2}$/.test(values.lastInspectionDate)
      )
        return "Last inspection date must be in YYYY-MM-DD format.";
      if (
        values.nextMaintenanceDate &&
        !/^\d{4}-\d{2}-\d{2}$/.test(values.nextMaintenanceDate)
      )
        return "Next maintenance date must be in YYYY-MM-DD format.";
      if (values.condition && values.condition.length > 30)
        return "Condition must be 30 characters or less.";
      if (values.notes && values.notes.length > 4000)
        return "Notes must be 4000 characters or less.";
    }
    if (activeModule.key === "systems") {
      if (values.companyName && values.companyName.length > 200)
        return "Company name must be 200 characters or less.";
      if (
        values.adminEmail &&
        !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.adminEmail)
      )
        return "Admin email must be a valid email address.";
      if (values.adminEmail && values.adminEmail.length > 254)
        return "Admin email must be 254 characters or less.";
      if (values.userRoles && values.userRoles.length > 1000)
        return "User roles must be 1000 characters or less.";
      if (values.passwordPolicy && values.passwordPolicy.length > 1000)
        return "Password policy must be 1000 characters or less.";
      if (values.notifications && values.notifications.length > 50)
        return "Notifications must be 50 characters or less.";
      if (values.timeZone && values.timeZone.length > 100)
        return "Time zone must be 100 characters or less.";
      if (values.integrationStatus && values.integrationStatus.length > 50)
        return "Integration status must be 50 characters or less.";
      if (values.backupSchedule && values.backupSchedule.length > 50)
        return "Backup schedule must be 50 characters or less.";
      if (values.systemStatus && values.systemStatus.length > 50)
        return "System status must be 50 characters or less.";
    }
    if (activeModule.key === "cellPhones") {
      if (values.deviceId && values.deviceId.length > 100)
        return "Device ID must be 100 characters or less.";
      if (values.operator && values.operator.length > 200)
        return "Operator must be 200 characters or less.";
      if (values.mobileNumber && !isValidPhoneNumber(values.mobileNumber))
        return "Mobile number may contain digits, a leading +, spaces, hyphens or parentheses, and must have at most 15 digits.";
      if (values.mobileNumber && values.mobileNumber.length > 50)
        return "Mobile number must be 50 characters or less.";
      if (values.deviceName && values.deviceName.length > 200)
        return "Device name must be 200 characters or less.";
      if (values.airportId && values.airportId.length > 100)
        return "Airport ID must be 100 characters or less.";
      if (values.notifications && values.notifications.length > 50)
        return "Notifications must be 50 characters or less.";
      if (
        values.connectionStatus &&
        !["Online", "Offline"].includes(values.connectionStatus)
      )
        return "Connection status must be Online or Offline.";
      if (values.lastActive && !isDateTimeLocal(values.lastActive))
        return "Last active must be a valid date and time.";
    }
    if (activeModule.key === "plcs") {
      if (values.plcId && values.plcId.length > 100)
        return "PLC ID must be 100 characters or less.";
      if (values.name && values.name.length > 200)
        return "PLC name must be 200 characters or less.";
      if (values.address && values.address.length > 200)
        return "Address must be 200 characters or less.";
      if (values.protocol && values.protocol.length > 50)
        return "Protocol must be 50 characters or less.";
      if (values.airportId && values.airportId.length > 100)
        return "Airport ID must be 100 characters or less.";
      if (values.approachId && values.approachId.length > 100)
        return "Approach ID must be 100 characters or less.";
      if (values.beltId && values.beltId.length > 100)
        return "Belt ID must be 100 characters or less.";
      if (
        values.connectionStatus &&
        !["Online", "Offline"].includes(values.connectionStatus)
      )
        return "Connection status must be Online or Offline.";
      if (
        values.lastCommunication &&
        !isDateTimeLocal(values.lastCommunication)
      )
        return "Last communication must be a valid date and time.";
      if (values.faultCode && values.faultCode.length > 100)
        return "Fault code must be 100 characters or less.";
    }
    if (activeModule.key === "radios") {
      if (values.radioId && values.radioId.length > 100)
        return "Radio ID must be 100 characters or less.";
      if (values.staffAssignment && values.staffAssignment.length > 200)
        return "Staff assignment must be 200 characters or less.";
      if (values.airportId && values.airportId.length > 100)
        return "Airport ID must be 100 characters or less.";
      if (values.approachId && values.approachId.length > 100)
        return "Approach ID must be 100 characters or less.";
      if (
        values.deviceStatus &&
        !["Assigned", "Available"].includes(values.deviceStatus)
      )
        return "Device status must be Assigned or Available.";
      if (values.communicationGroup && values.communicationGroup.length > 100)
        return "Communication group must be 100 characters or less.";
      if (values.maintenanceLog && values.maintenanceLog.length > 4000)
        return "Maintenance log must be 4000 characters or less.";
    }
    if (activeModule.key === "displays") {
      if (values.displayId && values.displayId.length > 100)
        return "Display ID must be 100 characters or less.";
      if (values.name && values.name.length > 200)
        return "Display name must be 200 characters or less.";
      if (values.airportId && values.airportId.length > 100)
        return "Airport ID must be 100 characters or less.";
      if (values.terminal && values.terminal.length > 100)
        return "Terminal must be 100 characters or less.";
      if (values.approachId && values.approachId.length > 100)
        return "Approach ID must be 100 characters or less.";
      if (values.beltId && values.beltId.length > 100)
        return "Belt ID must be 100 characters or less.";
      if (values.displayType && values.displayType.length > 100)
        return "Display type must be 100 characters or less.";
      if (
        values.connectionStatus &&
        !["Online", "Offline"].includes(values.connectionStatus)
      )
        return "Connection status must be Online or Offline.";
      if (values.screenLayout && values.screenLayout.length > 300)
        return "Screen layout must be 300 characters or less.";
    }
    if (activeModule.key === "activity") {
      if (values.action && values.action.length > 100)
        return "Action must be 100 characters or less.";
      if (values.entity && values.entity.length > 200)
        return "Entity must be 200 characters or less.";
      if (values.module && values.module.length > 50)
        return "Module must be 50 characters or less.";
      if (
        values.severity &&
        !["Info", "Warning", "Error"].includes(values.severity)
      )
        return "Severity must be Info, Warning, or Error.";
    }
    const uniqueKeys = {
      countries: "isoCode",
      states: "code",
      approaches: "code",
      belts: "beltId",
      sensors: "sensorId",
      bearings: "bearingId",
      plcs: "plcId",
      cellPhones: "deviceId",
      radios: "radioId",
      displays: "displayId",
    };
    const uniqueKey = uniqueKeys[activeModule.key];
    if (uniqueKey) {
      const message = duplicate(
        uniqueKey,
        `${uniqueKey === "isoCode" ? "ISO code" : uniqueKey} must be unique.`,
        (value) =>
          String(value || "")
            .trim()
            .toUpperCase(),
      );
      if (message) return message;
    }
    return "";
  };

  const validFieldKeys = (moduleKey) =>
    new Set((FIELDS[moduleKey] || []).map((f) => f[0]));

  const saveForm = async (event) => {
    event.preventDefault();
    const values = { ...formItem };
    if (activeModule.key === "airports") values.type = category;
    if (activeModule.key === "countries")
      values.classification =
        category === "Domestic" ? "Domestic base" : "International";
    if (activeModule.key === "iataCodes")
      values.code = values.code.trim().toUpperCase();
    if (activeModule.key === "countries")
      values.isoCode = values.isoCode.trim().toUpperCase();
    if (activeModule.key === "states")
      values.code = values.code.trim().toUpperCase();
    if (activeModule.key === "bagCounts")
      values.eventId = (values.eventId || "").trim();
    if (activeModule.key === "cellPhones" && values.mobileNumber)
      values.mobileNumber = normalizePhoneNumber(values.mobileNumber);
    const validationError = validateRecord(values);
    if (validationError) {
      notify(validationError, "error");
      return;
    }
    if (activeModule.key === "bagCounts") {
      if (values.countSource === "Sensor / PLC" && !values.eventId) {
        notify(
          "A unique sensor / counter event ID is required for sensor or PLC readings.",
          "error",
        );
        return;
      }
      if (
        values.eventId &&
        db.bagCounts.some(
          (item) =>
            item.id !== editingId &&
            item.eventId &&
            item.eventId.toLowerCase() === values.eventId.toLowerCase(),
        )
      ) {
        notify(
          "This counter event has already been recorded; duplicate signals are rejected.",
          "error",
        );
        return;
      }
      const previousBeltTotal = db.bagCounts
        .filter(
          (item) => item.id !== editingId && item.beltId === values.beltId,
        )
        .reduce((max, item) => Math.max(max, Number(item.totalCount) || 0), 0);
      if (
        values.countSource === "Sensor / PLC" &&
        Number(values.totalCount) <= previousBeltTotal
      ) {
        notify(
          "Sensor / PLC events must increase the belt total. Duplicate or out-of-order events were rejected.",
          "error",
        );
        return;
      }
    }
    if (activeModule.key === "systems") values.id = "system-config";
    const now = new Date().toISOString();
    const allowedKeys = validFieldKeys(activeModule.key);
    const filterRecord = (record) =>
      Object.fromEntries(
        Object.entries(record).filter(
          ([k]) =>
            allowedKeys.has(k) ||
            ["id", "createdAt", "updatedAt", "createdBy", "updatedBy"].includes(
              k,
            ),
        ),
      );
    const saved = editingId
      ? {
          ...filterRecord(
            recordContext(records.find((item) => item.id === editingId)),
          ),
          ...filterRecord(values),
          id: editingId,
          updatedAt: now,
          updatedBy: "Administrator",
        }
      : {
          ...filterRecord(values),
          id: uid(activeModule.key),
          createdAt: now,
          createdBy: "Administrator",
          updatedAt: now,
          updatedBy: "Administrator",
        };
    const source =
      activeModule.key === "beltRunning" ? "belts" : activeModule.key;
    let nextItems = [...db[source]];
    const existingIndex = nextItems.findIndex((item) => item.id === saved.id);
    if (existingIndex >= 0) nextItems[existingIndex] = saved;
    else nextItems.unshift(saved);
    const nextDb = { ...db, [source]: nextItems };

    if (activeModule.key === "beltRunning") {
      nextDb.belts = nextDb.belts.map((belt) =>
        belt.id === saved.id
          ? {
              ...belt,
              runningStatus: values.runningStatus,
              lastUpdated: values.lastUpdated,
              updatedAt: now,
              updatedBy: "Administrator",
            }
          : belt,
      );
    }
    if (activeModule.key === "iataCodes") {
      const airport = nextDb.airports.find(
        (item) => item.id === values.airportId,
      );
      if (airport)
        nextDb.airports = nextDb.airports.map((item) =>
          item.id === airport.id
            ? { ...item, updatedAt: now, updatedBy: "Administrator" }
            : item,
        );
    }
    if (activeModule.key === "sensors") {
      nextDb.belts = nextDb.belts.map((belt) =>
        belt.id === values.beltId ? { ...belt, sensorId: saved.id } : belt,
      );
    }
    if (activeModule.key === "plcs") {
      nextDb.belts = nextDb.belts.map((belt) =>
        belt.id === values.beltId ? { ...belt, plcId: saved.id } : belt,
      );
    }
    if (
      await persist(
        nextDb,
        editingId ? "Updated" : "Created",
        moduleRecordLabel(activeModule, saved),
      )
    )
      closeForm();
  };

  const relatedRecordsExist = (moduleKey, id) => {
    const references = {
      countries: [
        ["states", "countryId"],
        ["airports", "countryId"],
        ["approaches", "countryId"],
      ],
      states: [
        ["airports", "stateId"],
        ["approaches", "stateId"],
      ],
      airports: [
        ["iataCodes", "airportId"],
        ["approaches", "airportId"],
        ["belts", "airportId"],
        ["sensors", "airportId"],
        ["cellPhones", "airportId"],
        ["plcs", "airportId"],
        ["radios", "airportId"],
        ["displays", "airportId"],
        ["bagCounts", "airportId"],
      ],
      approaches: [
        ["belts", "approachId"],
        ["sensors", "approachId"],
        ["plcs", "approachId"],
        ["radios", "approachId"],
        ["displays", "approachId"],
      ],
      belts: [
        ["bagCounts", "beltId"],
        ["sensors", "beltId"],
        ["bearings", "beltId"],
        ["plcs", "beltId"],
        ["displays", "beltId"],
      ],
      plcs: [["sensors", "plcId"]],
    };
    return (references[moduleKey] || []).some(([child, key]) =>
      db[child].some((item) => item[key] === id),
    );
  };

  const deleteRecord = async (item) => {
    const target = recordContext(item);
    const label = moduleRecordLabel(activeModule, target);
    const relationModule =
      activeModule.key === "beltRunning" ? "belts" : activeModule.key;
    if (relatedRecordsExist(relationModule, target.id)) {
      notify(
        `${label} has linked records. Deactivate it instead of deleting it.`,
        "error",
      );
      return;
    }
    // Proceed with deletion without confirmation
    const source =
      activeModule.key === "beltRunning" ? "belts" : activeModule.key;
    const nextDb = {
      ...db,
      [source]: db[source].filter((record) => record.id !== target.id),
    };
    await persist(nextDb, "Deleted", label);
  };

  const deactivateRecord = async (item) => {
    const target = recordContext(item);
    if (!target.status) return;
    if (
      !window.confirm(`Deactivate ${moduleRecordLabel(activeModule, target)}?`)
    )
      return;
    const source =
      activeModule.key === "beltRunning" ? "belts" : activeModule.key;
    const nextDb = {
      ...db,
      [source]: db[source].map((record) =>
        record.id === target.id
          ? {
              ...record,
              status: "Inactive",
              updatedAt: new Date().toISOString(),
              updatedBy: "Administrator",
            }
          : record,
      ),
    };
    await persist(
      nextDb,
      "Deactivated",
      moduleRecordLabel(activeModule, target),
    );
  };

  const exportPdf = () => {
    try {
      const headers = activeModule.columns.map((column) => column[1]);
      const rows = filteredRecords.map((record) =>
        activeModule.columns.map((column) => rowValue(db, record, column)),
      );
      exportWorkspacePdf({
        title: activeModule.label,
        category,
        headers,
        rows,
        filename: `${activeModule.key}-${category.toLowerCase().replace(/\s+/g, "-")}.pdf`,
      });
      notify(`${activeModule.label} exported successfully.`, "success");
    } catch (error) {
      console.error("Unable to export admin workspace records as PDF.", error);
      notify(
        error.message || "Unable to export admin workspace records as PDF.",
        "error",
      );
    }
  };

  const dashboardMetrics = useMemo(() => {
    const relevant = (table) =>
      table.filter((item) => !item.airportId || airportIds.has(item.airportId));
    const activeBelts = relevant(db.belts).filter(
      (item) => item.status !== "Inactive",
    );
    const running = activeBelts.filter(
      (item) => item.runningStatus === "Running",
    ).length;
    const sensorItems = relevant(db.sensors);
    const plcItems = relevant(db.plcs);
    const faults =
      activeBelts.filter((item) =>
        ["Fault", "Maintenance"].includes(item.runningStatus),
      ).length +
      sensorItems.filter(
        (item) =>
          item.faultStatus === "Fault" || item.connectionStatus === "Offline",
      ).length +
      plcItems.filter((item) => item.faultCode && item.faultCode !== "None")
        .length;
    return {
      countries: new Set(relevant(db.airports).map((item) => item.countryId))
        .size,
      states: new Set(relevant(db.airports).map((item) => item.stateId)).size,
      airports: airportsInCategory.length,
      approaches: relevant(db.approaches).length,
      belts: activeBelts.length,
      running,
      stopped: activeBelts.filter((item) => item.runningStatus === "Stopped")
        .length,
      beltFaults: activeBelts.filter((item) =>
        ["Fault", "Maintenance", "Offline / Unknown"].includes(
          item.runningStatus,
        ),
      ).length,
      bags: relevant(db.bagCounts).reduce(
        (sum, item) => sum + Number(item.totalCount || 0),
        0,
      ),
      sensorsOnline: sensorItems.filter(
        (item) => item.connectionStatus === "Online",
      ).length,
      sensorsOffline: sensorItems.filter(
        (item) => item.connectionStatus === "Offline",
      ).length,
      plcsOnline: plcItems.filter((item) => item.connectionStatus === "Online")
        .length,
      plcsOffline: plcItems.filter(
        (item) => item.connectionStatus === "Offline",
      ).length,
      faults,
    };
  }, [db, airportIds, airportsInCategory.length]);

  const dashboardCards = [
    [
      "Countries",
      dashboardMetrics.countries,
      LuBuilding2,
      "countries",
      "Master records in selected airport scope",
    ],
    [
      "States",
      dashboardMetrics.states,
      LuMapPin,
      "states",
      "Linked to selected airport scope",
    ],
    [
      "Airports",
      dashboardMetrics.airports,
      LuPlane,
      "airports",
      "Registered locations",
    ],
    [
      "Approach locations",
      dashboardMetrics.approaches,
      LuMapPin,
      "approaches",
      "Terminal baggage areas",
    ],
    [
      "Total belts",
      dashboardMetrics.belts,
      LuBoxes,
      "belts",
      "Calculated from active belt records",
    ],
    [
      "Running belts",
      dashboardMetrics.running,
      LuActivity,
      "beltRunning",
      "Status from registered belt records",
    ],
    [
      "Stopped belts",
      dashboardMetrics.stopped,
      LuArrowDownRight,
      "beltRunning",
      "Registered and currently stopped",
    ],
    [
      "Fault / maintenance belts",
      dashboardMetrics.beltFaults,
      LuTriangleAlert,
      "beltRunning",
      "Fault, maintenance or offline status",
    ],
    [
      "Total bags",
      dashboardMetrics.bags.toLocaleString(),
      LuPackage,
      "bagCounts",
      "Sum of recorded counter totals",
    ],
    [
      "Sensors online / offline",
      `${dashboardMetrics.sensorsOnline} / ${dashboardMetrics.sensorsOffline}`,
      LuSignal,
      "sensors",
      "Current connection records",
    ],
    [
      "PLCs online / offline",
      `${dashboardMetrics.plcsOnline} / ${dashboardMetrics.plcsOffline}`,
      LuFactory,
      "plcs",
      "Last recorded PLC connection",
    ],
    [
      "Equipment fault alerts",
      dashboardMetrics.faults,
      LuTriangleAlert,
      "sensors",
      "Belt, sensor and controller alerts",
    ],
  ];

  const fieldOptions = (field) => {
    const [key, , type] = field;
    if (type !== "relation") return [];
    const related = filteredOptions(activeModule.key, key, formItem);
    return related.map((item) => ({
      value: item.id,
      label:
        item.name ||
        item.airportName ||
        item.beltId ||
        item.code ||
        item.sensorId ||
        item.plcId ||
        item.bearingId ||
        item.deviceId ||
        item.radioId ||
        item.displayId ||
        item.isoCode,
    }));
  };

  const moduleGroups = [...new Set(MODULES.map((module) => module.group))];
  const ActiveIcon = activeModule.icon;
  const upcomingMaintenance = db.bearings
    .filter(
      (item) =>
        item.nextMaintenanceDate &&
        item.nextMaintenanceDate <= dateOnly(30) &&
        item.condition !== "Good",
    )
    .slice(0, 5);
  const recentActivity = db.activity.slice(0, 6);

  if (!dbReady && !isProfileRoute) {
    return (
      <AdminWorkspaceLoading
        error={dbLoadError}
        onRetry={() => setWorkspaceLoadAttempt((attempt) => attempt + 1)}
        onLogout={onLogout}
      />
    );
  }

  return (
    <div className="admin-shell admin-workspace">
      <AdminSidebar
        user={user}
        category={category}
        onCategoryChange={setCategory}
        sidebarOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        onLogout={onLogout}
        moduleGroups={moduleGroups}
        modules={MODULES}
      />

      <div className="admin-main">
        <AdminTopbar
          title={isProfileRoute ? "My profile" : activeModule.label}
          onOpenNavigation={() => setSidebarOpen(true)}
        />
        <main className="admin-content workspace-content">
          {isProfileRoute ? (
            <AdminProfilePage
              user={user}
              onBack={() => navigate("/admin/dashboard")}
            />
          ) : (
            <>
              <div className="admin-page-heading">
                <div>
                  <div className="admin-eyebrow">
                    <LuActivity size={14} /> {category.toUpperCase()} Â· AIRPORT
                    OPERATIONS
                  </div>
                  <h3>{activeModule.label}</h3>
                  <p>
                    {activeModule.key === "dashboard"
                      ? "A live-at-a-glance view of airport belt operations and equipment health."
                      : activeModule.description}
                  </p>
                </div>
                {activeModule.key !== "dashboard" &&
                  activeModule.key !== "beltRunning" && (
                    <button
                      className="admin-primary-button"
                      onClick={() => openForm(null)}
                    >
                      <LuPlus size={17} /> Add{" "}
                      {activeModule.key === "iataCodes"
                        ? "IATA code"
                        : activeModule.key === "approaches"
                          ? "location"
                          : activeModule.key === "belts"
                            ? "belt"
                            : activeModule.key === "bagCounts"
                              ? "count"
                              : activeModule.key === "cellPhones"
                                ? "device"
                                : activeModule.key === "radios"
                                  ? "radio"
                                  : activeModule.key === "plcs"
                                    ? "PLC"
                                    : activeModule.label.toLowerCase()}
                    </button>
                  )}
              </div>

              {activeModule.key === "dashboard" ? (
                <>
                  <section
                    className="live-bag-counter"
                    aria-label="Live bag count"
                  >
                    <div className="live-bag-counter-summary">
                      <span className="live-bag-counter-icon">
                        <LuPackage size={22} />
                      </span>
                      <div>
                        <span className="live-bag-counter-kicker">
                          LIVE BAG COUNT Â· PLANNED THROUGHPUT
                        </span>
                        <strong>
                          {liveBagCount.toLocaleString()}{" "}
                          <small>
                            / {bagCounter.target.toLocaleString()} bags
                          </small>
                        </strong>
                        <span className="live-bag-counter-status">
                          {isCounterRunning
                            ? "Counting toward target"
                            : liveBagCount >= bagCounter.target
                              ? "Target reached"
                              : "Ready to start"}
                        </span>
                      </div>
                    </div>
                    <div className="live-bag-counter-progress">
                      <div className="live-bag-counter-progress-heading">
                        <span>Progress</span>
                        <strong>{Math.floor(counterProgress)}%</strong>
                      </div>
                      <div
                        className="live-bag-counter-track"
                        role="progressbar"
                        aria-label="Bag target progress"
                        aria-valuemin="0"
                        aria-valuemax="100"
                        aria-valuenow={Math.floor(counterProgress)}
                      >
                        <i style={{ width: `${counterProgress}%` }} />
                      </div>
                      <span className="live-bag-counter-remaining">
                        {isCounterRunning
                          ? `${formatRemainingTime(counterRemainingMs)} remaining`
                          : `Duration: ${bagCounter.durationMinutes} min`}
                      </span>
                    </div>
                    <form
                      className="live-bag-counter-form"
                      onSubmit={startBagCounter}
                      onInvalid={showValidationToast}
                    >
                      <label>
                        <span>Target bags</span>
                        <input
                          type="number"
                          min="1"
                          max={Number.MAX_SAFE_INTEGER}
                          step="1"
                          required
                          value={counterTarget}
                          onChange={(event) =>
                            setCounterTarget(event.target.value)
                          }
                        />
                      </label>
                      <label>
                        <span>Duration (minutes)</span>
                        <input
                          type="number"
                          min="1"
                          max={Math.floor(Number.MAX_SAFE_INTEGER / 60000)}
                          step="1"
                          required
                          value={counterDuration}
                          onChange={(event) =>
                            setCounterDuration(event.target.value)
                          }
                        />
                      </label>
                      <button type="submit" className="admin-primary-button">
                        {bagCounter.startedAt === null
                          ? "Start count"
                          : "Restart count"}
                      </button>
                    </form>
                  </section>
                  <section
                    className="dashboard-overview-grid"
                    aria-label="Operations overview"
                  >
                    {dashboardCards.map(
                      ([label, value, Icon, target, caption]) => (
                        <button
                          className={`dashboard-metric-card${label.includes("fault") ? " is-alert" : ""}`}
                          key={label}
                          onClick={() => navigate(`/admin/${target}`)}
                        >
                          <span className="dashboard-metric-icon">
                            <Icon size={18} />
                          </span>
                          <span className="dashboard-metric-label">
                            {label}
                          </span>
                          <strong>{value}</strong>
                          <small>{caption}</small>
                          <LuArrowUpRight
                            className="dashboard-metric-open"
                            size={15}
                          />
                        </button>
                      ),
                    )}
                  </section>
                  <div className="dashboard-lower-grid">
                    <section className="admin-records-card dashboard-activity">
                      <div className="admin-records-heading">
                        <div>
                          <h2>Recent activity</h2>
                          <p>Latest changes and operational events</p>
                        </div>
                        <button
                          className="workspace-text-link"
                          onClick={() => navigate("/admin/systems")}
                        >
                          Audit &amp; system <LuArrowRight size={14} />
                        </button>
                      </div>
                      {recentActivity.length ? (
                        <div className="workspace-activity-list">
                          {recentActivity.map((event) => (
                            <div
                              className="workspace-activity-row"
                              key={event.id}
                            >
                              <span
                                className={`workspace-activity-icon ${event.severity?.toLowerCase()}`}
                              >
                                <LuActivity size={15} />
                              </span>
                              <span className="workspace-activity-copy">
                                <strong>{event.action}</strong>
                                <small>
                                  {event.entity} Â· {event.createdBy || "System"}
                                </small>
                              </span>
                              <time>{formatTime(event.createdAt)}</time>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="workspace-inline-empty">
                          No activity has been recorded yet.
                        </div>
                      )}
                    </section>
                    <section className="admin-records-card dashboard-maintenance">
                      <div className="admin-records-heading">
                        <div>
                          <h2>Maintenance alerts</h2>
                          <p>Due or overdue bearing inspections</p>
                        </div>
                        <LuBatteryWarning
                          className="workspace-section-icon"
                          size={19}
                        />
                      </div>
                      {upcomingMaintenance.length ? (
                        <div className="workspace-maintenance-list">
                          {upcomingMaintenance.map((item) => (
                            <button
                              key={item.id}
                              onClick={() => navigate("/admin/bearings")}
                            >
                              <span className="workspace-maintenance-mark">
                                <LuTriangleAlert size={15} />
                              </span>
                              <span>
                                <strong>{item.bearingId}</strong>
                                <small>
                                  {idLabel(db, "belts", item.beltId)} Â· Due{" "}
                                  {item.nextMaintenanceDate}
                                </small>
                              </span>
                              <LuArrowUpRight size={15} />
                            </button>
                          ))}
                        </div>
                      ) : (
                        <div className="workspace-inline-empty">
                          No upcoming maintenance alerts.
                        </div>
                      )}
                      <button
                        className="workspace-card-footer"
                        onClick={() => navigate("/admin/bearings")}
                      >
                        Open maintenance register <LuArrowRight size={14} />
                      </button>
                    </section>
                  </div>
                  <div className="workspace-dashboard-footnote">
                    <LuSignal size={14} /> Status is based on the saved
                    workspace records; live PLC telemetry is not connected.
                  </div>
                </>
              ) : (
                <>
                  <section className="workspace-module-strip">
                    <div className="workspace-module-mark">
                      <ActiveIcon size={21} />
                    </div>

                    <div className="workspace-module-intro">
                      <span>{activeModule.group.toUpperCase()} MODULE</span>
                      <strong>
                        {records.length.toLocaleString()}{" "}
                        {records.length === 1 ? "record" : "records"}
                      </strong>
                    </div>

                    <div className="workspace-module-context">
                      <span>
                        {category === "Domestic"
                          ? "Airport scope"
                          : "Selected scope"}
                      </span>
                      <strong>
                        {category} Â· {airportsInCategory.length} airports
                      </strong>
                    </div>
                  </section>

                  <section className="admin-records-card workspace-table-card">
                    <div className="admin-records-heading workspace-table-heading">
                      <div>
                        <h2>{activeModule.label} register</h2>
                        <p>
                          {filteredRecords.length
                            ? `${filteredRecords.length} records Â· Page ${page} of ${totalPages}`
                            : "No records match this search or filter"}
                        </p>
                      </div>
                      <div className="workspace-table-tools">
                        <label className="admin-search">
                          <LuSearch size={16} />
                          <input
                            value={search}
                            onChange={(event) => setSearch(event.target.value)}
                            placeholder={`Search ${activeModule.label.toLowerCase()}...`}
                            aria-label={`Search ${activeModule.label}`}
                          />
                          {search && (
                            <button
                              type="button"
                              aria-label="Clear search"
                              onClick={() => setSearch("")}
                            >
                              <LuX size={14} />
                            </button>
                          )}
                        </label>
                        {moduleHasStatus && (
                          <label className="workspace-filter">
                            <select
                              aria-label="Filter by status"
                              value={statusFilter}
                              onChange={(event) =>
                                setStatusFilter(event.target.value)
                              }
                            >
                              <option>All statuses</option>
                              {[
                                ...new Set(
                                  records
                                    .map((item) =>
                                      recordStatus(activeModule, item),
                                    )
                                    .filter(Boolean),
                                ),
                              ].map((value) => (
                                <option key={value}>{value}</option>
                              ))}
                            </select>
                          </label>
                        )}
                        <button
                          className="workspace-export-button"
                          onClick={exportPdf}
                        >
                          <LuDownload size={15} /> Export PDF
                        </button>
                      </div>
                    </div>
                    {pageRecords.length ? (
                      <div className="admin-table-wrap workspace-table-wrap">
                        <table className="admin-table workspace-data-table">
                          <thead>
                            <tr>
                              {activeModule.columns.map((column) => (
                                <th key={column[0]}>{column[1]}</th>
                              ))}
                              <th className="workspace-actions-head">
                                ACTIONS
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {pageRecords.map((item) => (
                              <tr key={item.id}>
                                {activeModule.columns.map((column) => {
                                  const value = rowValue(db, item, column);
                                  const statusColumn = [
                                    "status",
                                    "runningStatus",
                                    "connectionStatus",
                                    "condition",
                                    "faultStatus",
                                    "deviceStatus",
                                    "systemStatus",
                                  ].includes(column[0]);
                                  return (
                                    <td key={column[0]}>
                                      {statusColumn ? (
                                        <span
                                          className={`workspace-status-pill ${/fault|offline|inactive|maintenance|required|stopped/i.test(value) ? "is-warning" : /warning|degraded|partial/i.test(value) ? "is-pending" : "is-good"}`}
                                        >
                                          <i />
                                          {value}
                                        </span>
                                      ) : (
                                        <span
                                          className={
                                            [
                                              "code",
                                              "beltId",
                                              "sensorId",
                                              "plcId",
                                              "bearingId",
                                              "deviceId",
                                              "radioId",
                                              "displayId",
                                            ].includes(column[0])
                                              ? "workspace-code-value"
                                              : ""
                                          }
                                        >
                                          {value.length > 72
                                            ? `${value.slice(0, 69)}...`
                                            : value}
                                        </span>
                                      )}
                                    </td>
                                  );
                                })}
                                <td>
                                  <div className="admin-row-actions">
                                    <button
                                      title="View details"
                                      aria-label={`View ${moduleRecordLabel(activeModule, item)}`}
                                      onClick={() => setViewItem(item)}
                                    >
                                      <LuArrowUpRight size={15} />
                                    </button>
                                    <button
                                      title="Edit record"
                                      aria-label={`Edit ${moduleRecordLabel(activeModule, item)}`}
                                      onClick={() => openForm(item)}
                                    >
                                      <LuPencil size={14} />
                                    </button>
                                    {item.status === "Active" && (
                                      <button
                                        title="Deactivate"
                                        aria-label={`Deactivate ${moduleRecordLabel(activeModule, item)}`}
                                        onClick={() => deactivateRecord(item)}
                                      >
                                        <LuShieldCheck size={14} />
                                      </button>
                                    )}
                                    <button
                                      className="delete"
                                      title="Delete record"
                                      aria-label={`Delete record ${moduleRecordLabel(activeModule, item)}`}
                                      onClick={() => setDeleteItem(item)}
                                    >
                                      <LuTrash2 size={14} />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="admin-empty-state">
                        <span className="admin-empty-icon">
                          <ActiveIcon size={22} />
                        </span>
                        <h3>
                          {search || statusFilter !== "All statuses"
                            ? "No matching records"
                            : `No ${activeModule.label.toLowerCase()} records`}
                        </h3>
                        <p>
                          {search || statusFilter !== "All statuses"
                            ? "Clear or change your search and status filters."
                            : activeModule.key === "beltRunning"
                              ? "Register a belt first; operating status is monitored per registered belt."
                              : `Create a ${activeModule.label.toLowerCase()} record to start managing this module.`}
                        </p>
                        {!search && activeModule.key === "beltRunning" ? (
                          <button
                            className="admin-primary-button"
                            onClick={() => navigate("/admin/belts")}
                          >
                            <LuBoxes size={15} /> Register a belt
                          </button>
                        ) : (
                          !search && (
                            <button
                              className="admin-primary-button"
                              onClick={() => openForm(null)}
                            >
                              <LuPlus size={15} /> Add record
                            </button>
                          )
                        )}
                      </div>
                    )}
                    {filteredRecords.length > PAGE_SIZE && (
                      <div className="workspace-pagination">
                        <span>
                          Showing {(page - 1) * PAGE_SIZE + 1}â€“
                          {Math.min(page * PAGE_SIZE, filteredRecords.length)}{" "}
                          of {filteredRecords.length}
                        </span>
                        <div>
                          <button
                            disabled={page <= 1}
                            onClick={() => setPage(page - 1)}
                          >
                            Previous
                          </button>
                          <span>
                            {page} / {totalPages}
                          </span>
                          <button
                            disabled={page >= totalPages}
                            onClick={() => setPage(page + 1)}
                          >
                            Next
                          </button>
                        </div>
                      </div>
                    )}
                  </section>
                </>
              )}
            </>
          )}
        </main>
      </div>

      {formItem && (
        <div
          className="admin-modal-backdrop"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) closeForm();
          }}
        >
          <section
            className="admin-modal workspace-form-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="workspace-form-title"
          >
            <header className="admin-modal-header">
              <div>
                <span className="admin-modal-kicker">
                  {activeModule.group.toUpperCase()} Â· {category.toUpperCase()}
                </span>
                <h2 id="workspace-form-title">
                  {editingId
                    ? `Edit ${activeModule.label}`
                    : `Add ${activeModule.label}`}
                </h2>
                <p>{activeModule.description}</p>
              </div>
              <button
                className="admin-icon-button"
                aria-label="Close form"
                onClick={closeForm}
              >
                <LuX size={19} />
              </button>
            </header>
            <form onSubmit={saveForm} onInvalid={showValidationToast}>
              <div className="workspace-form-grid">
                {pageFields.map((field) => {
                  const [key, label, type, required, options] = field;
                  const eventIdRequired =
                    activeModule.key === "bagCounts" &&
                    key === "eventId" &&
                    formItem.countSource === "Sensor / PLC";
                  if (type === "relation")
                    return (
                      <label className="workspace-form-field" key={key}>
                        <span>
                          {label}
                          {required && <i> *</i>}
                        </span>
                        <select
                          required={required}
                          value={formItem[key] || ""}
                          onChange={(event) => {
                            const value = event.target.value;
                            setFormItem((current) => {
                              const next = { ...current, [key]: value };
                              if (key === "countryId") {
                                next.stateId = "";
                                if (activeModule.key === "approaches")
                                  next.airportId = "";
                              }
                              if (
                                key === "stateId" &&
                                activeModule.key === "approaches"
                              )
                                next.airportId = "";
                              if (
                                key === "airportId" &&
                                ["sensors", "plcs", "approaches"].includes(
                                  activeModule.key,
                                )
                              ) {
                                next.approachId = "";
                                next.beltId = "";
                              }
                              if (
                                key === "approachId" &&
                                ["sensors", "plcs"].includes(activeModule.key)
                              )
                                next.beltId = "";
                              return next;
                            });
                          }}
                        >
                          <option value="">Select {label.toLowerCase()}</option>
                          {fieldOptions(field).map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </label>
                    );
                  if (type === "select") {
                    const selectOptions =
                      activeModule.key === "airports" && key === "type"
                        ? [category]
                        : activeModule.key === "countries" &&
                            key === "classification"
                          ? [
                              category === "Domestic"
                                ? "Domestic base"
                                : "International",
                            ]
                          : options;
                    return (
                      <label className="workspace-form-field" key={key}>
                        <span>
                          {label}
                          {(required || eventIdRequired) && <i> *</i>}
                        </span>
                        <select
                          required={required || eventIdRequired}
                          value={formItem[key] || ""}
                          onChange={(event) =>
                            setFormItem({
                              ...formItem,
                              [key]: event.target.value,
                            })
                          }
                        >
                          <option value="">Select {label.toLowerCase()}</option>
                          {selectOptions.map((option) => (
                            <option key={option}>{option}</option>
                          ))}
                        </select>
                      </label>
                    );
                  }
                  if (type === "textarea")
                    return (
                      <label className="workspace-form-field is-wide" key={key}>
                        <span>
                          {label}
                          {required && <i> *</i>}
                        </span>
                        <textarea
                          required={required}
                          maxLength={FIELD_MAX_LENGTHS[activeModule.key]?.[key]}
                          rows={3}
                          placeholder={
                            typeof options === "string" ? options : ""
                          }
                          value={formItem[key] || ""}
                          onChange={(event) =>
                            setFormItem({
                              ...formItem,
                              [key]: event.target.value,
                            })
                          }
                        />
                      </label>
                    );
                  return (
                    <label className="workspace-form-field" key={key}>
                      <span>
                        {label}
                        {(required || eventIdRequired) && <i> *</i>}
                      </span>
                      <input
                        required={required || eventIdRequired}
                        type={type}
                        min={type === "number" ? 0 : undefined}
                        step={type === "number" ? "any" : undefined}
                        maxLength={FIELD_MAX_LENGTHS[activeModule.key]?.[key]}
                        pattern={
                          key === "code" && activeModule.key === "iataCodes"
                            ? "[A-Za-z]{3}"
                            : undefined
                        }
                        placeholder={typeof options === "string" ? options : ""}
                        value={formItem[key] || ""}
                        onChange={(event) =>
                          setFormItem({
                            ...formItem,
                            [key]:
                              type === "number"
                                ? event.target.value === ""
                                  ? ""
                                  : Number(event.target.value)
                                : key === "code" &&
                                    activeModule.key === "iataCodes"
                                  ? event.target.value.toUpperCase()
                                  : event.target.value,
                          })
                        }
                      />
                    </label>
                  );
                })}
              </div>
              <div className="admin-modal-footer ">
                <div>
                  <button
                    type="button"
                    className="admin-secondary-button"
                    onClick={closeForm}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="admin-primary-button">
                    <LuCheck size={15} />{" "}
                    {editingId ? "Save changes" : "Create record"}
                  </button>
                </div>
              </div>
            </form>
          </section>
        </div>
      )}

      {viewItem && (
        <div
          className="admin-modal-backdrop"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setViewItem(null);
          }}
        >
          <section
            className="admin-modal workspace-details-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="workspace-details-title"
          >
            <header className="admin-modal-header">
              <div>
                <span className="admin-modal-kicker">
                  {activeModule.group.toUpperCase()} Â· RECORD DETAILS
                </span>
                <h2 id="workspace-details-title">
                  {moduleRecordLabel(activeModule, viewItem)}
                </h2>
                <p>
                  {activeModule.label} Â· {category}
                </p>
              </div>
              <button
                className="admin-icon-button"
                aria-label="Close details"
                onClick={() => setViewItem(null)}
              >
                <LuX size={19} />
              </button>
            </header>
            <div className="workspace-details-grid">
              {pageFields.map((field) => {
                const [key, label] = field;
                const value = viewItem[key];
                const display =
                  field[2] === "relation"
                    ? idLabel(db, field[4], value)
                    : key === "mobileNumber"
                      ? value
                        ? `â€¢â€¢â€¢â€¢â€¢â€¢${value.slice(-4)}`
                        : "Not provided"
                      : value && typeof value === "object"
                        ? JSON.stringify(value)
                        : String(value || "â€”");
                return (
                  <div key={key}>
                    <span>{label}</span>
                    <strong>{display}</strong>
                  </div>
                );
              })}
              {viewItem.createdAt && (
                <div>
                  <span>Created at Â· by</span>
                  <strong>
                    {formatTime(viewItem.createdAt)} Â·{" "}
                    {viewItem.createdBy || "Administrator"}
                  </strong>
                </div>
              )}
              {viewItem.updatedAt && (
                <div>
                  <span>Updated at Â· by</span>
                  <strong>
                    {formatTime(viewItem.updatedAt)} Â·{" "}
                    {viewItem.updatedBy || "Administrator"}
                  </strong>
                </div>
              )}
            </div>
            <div className="admin-modal-footer">
              <span>Record ID Â· {viewItem.id}</span>
              <div>
                <button
                  className="admin-secondary-button"
                  onClick={() => setViewItem(null)}
                >
                  Close
                </button>
                <button
                  className="admin-primary-button"
                  onClick={() => {
                    const item = viewItem;
                    setViewItem(null);
                    openForm(item);
                  }}
                >
                  <LuPencil size={14} /> Edit
                </button>
              </div>
            </div>
          </section>
        </div>
      )}
      {deleteItem && (
        <div
          className="admin-modal-backdrop"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setDeleteItem(null);
          }}
        >
          <section
            className="admin-modal workspace-delete-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="delete-title"
          >
            <header className="admin-modal-header">
              <div>
                <span className="admin-modal-kicker">
                  {activeModule.group.toUpperCase()} Â· DELETE RECORD
                </span>
                <h2 id="delete-title">
                  Delete {moduleRecordLabel(activeModule, deleteItem)}?
                </h2>
                <p>
                  {activeModule.label} Â· {category}
                </p>
              </div>
              <button
                className="admin-icon-button"
                aria-label="Close"
                onClick={() => setDeleteItem(null)}
              >
                <LuX size={19} />
              </button>
            </header>
            <div className="admin-modal-footer">
              <div>
                <button
                  className="admin-secondary-button"
                  onClick={() => setDeleteItem(null)}
                >
                  Cancel
                </button>
                <button
                  className="admin-primary-button"
                  onClick={() => {
                    const item = deleteItem;
                    setDeleteItem(null);
                    deleteRecord(item);
                  }}
                >
                  Delete
                </button>
              </div>
            </div>
          </section>
        </div>
      )}
    </div>
  );
}

export default AdminWorkspacePage;
